import React, { useEffect, useMemo, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { adminApi, auth, sessionProfile, validateSession } from "@/lib/api";
import { toast } from "sonner";
import { APP_ICON_URL } from "@/lib/assets";
import { Users, UserRound, BarChart3, ShieldAlert, Database, Settings, LogOut, ShoppingBag, Bell, Landmark, Menu, X, Boxes, CreditCard, Trophy, ArrowLeftRight, CircleDollarSign, Sparkles, Brain } from "lucide-react";

import FamilyAiWorkspace from '@/components/ai/FamilyAiWorkspace';
import FamilyAiSettings from '@/pages/admin/FamilyAiSettings';
import UsersPage from "@/pages/admin/UsersPage";
import StatsPage from "@/pages/admin/StatsPage";
import BackupPage from "@/pages/admin/BackupPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import MarketplacePage from "@/pages/admin/MarketplacePage";
import NotificationsAdminPage from "@/pages/admin/NotificationsAdminPage";
import ExternalMoneyAdminPage from "@/pages/admin/ExternalMoneyAdminPage";
import { BankFundsSection, OperationsApprovalsSection, LogsCenter } from "@/pages/admin/AdminCenters";
import SubscriptionCenter from "@/pages/admin/SubscriptionCenter";
import GamificationAdminPage from "@/pages/admin/GamificationAdminPage";
import BoxesAdminPage from "@/pages/admin/BoxesAdminPage";
import ErrorBoundary from "@/components/ui/ErrorBoundary";
import { AdminAccessProvider } from '@/context/AdminAccessContext';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [switchingPersonal, setSwitchingPersonal] = useState(false);
  const accessState = data => ({ loading: !data, fullAccess: data?.mode === 'FULL', permissions: data?.permissions || [], sections: data?.sections || [] });
  const [access, setAccess] = useState(() => accessState(sessionProfile(auth.getAdmin())));
  useEffect(() => {
    let live = true;
    validateSession('admin', auth.getAdmin()).then(data => { if (live) setAccess(accessState(data.profile)); }).catch(() => { if (live) setAccess(accessState({})); });
    const updated = event => { if (event.detail.kind === 'admin') setAccess(accessState(event.detail.profile)); };
    window.addEventListener('mb:session-validated', updated);
    return () => { live = false; window.removeEventListener('mb:session-validated', updated); };
  }, []);

  const handleLogout = async () => {
    try { await auth.logout('admin'); navigate('/admin'); }
    catch { toast.error('تعذر تسجيل الخروج. تحقق من الاتصال وأعد المحاولة.'); }
  };

  const switchToPersonal = async () => {
    if (switchingPersonal) return;
    setSwitchingPersonal(true);
    try {
      const { data } = await adminApi.post('/admin/manager/switch-to-personal');
      auth.setUser(data.token);
      auth.clearAdmin();
      navigate(data.must_change_password ? '/user/password-required' : '/user/dashboard');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'تعذر فتح الحساب الشخصي');
    } finally { setSwitchingPersonal(false); }
  };

  // The order is intentional: it mirrors the operating flow used by admins
  // and is shared by desktop and mobile navigation.
  const navGroups = [
    { label: 'الإدارة', items: [
      { path: "/admin/dashboard/stats", section: 'dashboard', label: "لوحة التحكم", icon: BarChart3 },
      { path: "/admin/dashboard/users", section: 'users', label: "المستخدمون", icon: Users },
      { path: '/admin/dashboard/operations-approvals', section: 'operations', label: 'العمليات والموافقات', icon: ArrowLeftRight },
      { path: '/admin/dashboard/subscriptions', section: 'subscriptions', label: 'الاشتراكات', icon: CreditCard },
      { path: '/admin/dashboard/bank-funds', section: 'bank_money', label: 'أموال البنك', icon: CircleDollarSign },
      { path: '/admin/dashboard/boxes-goals', section: 'boxes_goals', label: 'الصناديق والأهداف', icon: Boxes },
      { path: '/admin/dashboard/external-money', section: 'outside_money', label: 'إدارة أموالي +', icon: Landmark },
      { path: '/admin/dashboard/marketplace', section: 'store', label: 'إدارة المتجر', icon: ShoppingBag },
      { path: '/admin/dashboard/gamification', section: 'gamification', label: 'Gamification', icon: Trophy },
      { path: '/admin/dashboard/family-ai', section: null, label: 'بنك العائلة AI', icon: Sparkles },
      { path: '/admin/dashboard/family-ai-settings', section: 'settings', fullOnly: true, label: 'إعدادات AI', icon: Brain },
      { path: '/admin/dashboard/notifications', section: 'notifications', label: 'الإشعارات', icon: Bell },
      { path: '/admin/dashboard/logs', section: 'logs', label: 'السجلات', icon: ShieldAlert },
      { path: "/admin/dashboard/backup", section: 'backup', label: "النسخ الاحتياطي", icon: Database },
      { path: "/admin/dashboard/settings", section: 'settings', label: "الإعدادات العامة", icon: Settings }
    ]}
  ];

  const allowedSections = useMemo(() => new Set(access.sections.map(section => section.id)), [access]);
  const firstAllowedPath = navGroups[0].items.find(item => access.fullAccess || allowedSections.has(item.section))?.path?.replace('/admin/dashboard/', '') || 'stats';
  const guarded = (section, element) => !section || access.loading || access.fullAccess || allowedSections.has(section) ? element : <Navigate to={`../${firstAllowedPath}`} replace />;
  const renderNavigation = (mobile = false) => (
    <nav className={mobile ? 'space-y-5' : 'space-y-6'} aria-label="التنقل الإداري">
      {navGroups.map(group => <section key={group.label}>
        <h2 className="px-3 pb-2 text-[10px] font-black tracking-[0.16em] text-[var(--color-text-secondary)]">{group.label}</h2>
        <div className="space-y-1">
          {group.items.filter(item => (!item.fullOnly || access.fullAccess) && (!item.section || access.fullAccess || allowedSections.has(item.section))).map(item => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return <Link key={item.path} to={item.path} onClick={() => setMobileNavOpen(false)} aria-current={isActive ? 'page' : undefined} className={`family-nav-item flex min-h-11 items-center gap-3 rounded-xl px-3 py-2.5 text-xs font-bold transition-all ${isActive ? 'bg-[image:var(--gradient-electric)] text-white shadow-[var(--glow-blue)] border-s-4 border-[var(--color-accent)]' : 'text-[var(--color-text-primary)] hover:bg-[var(--color-surface-secondary)]'}`}>
              <Icon className={`h-4 w-4 shrink-0 ${isActive ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-secondary)]'}`} />
              <span className="min-w-0 leading-5">{item.label}</span>
            </Link>;
          })}
        </div>
      </section>)}
    </nav>
  );

  return (
    <AdminAccessProvider value={access}><div className="family-auth-shell family-admin-dashboard-shell min-h-screen bg-[var(--color-background)] text-[var(--color-text-primary)] flex flex-col font-sans">
      {/* Top Navbar */}
      <header className="family-admin-header text-white px-3 sm:px-6 py-2.5 sm:py-3 shadow-md flex items-center justify-between gap-3 sticky top-0 z-30">
        <div className="flex min-w-0 items-center gap-2.5 sm:gap-3">
          <button onClick={() => setMobileNavOpen(true)} className="lg:hidden fb-header-control" aria-label="فتح قائمة الإدارة"><Menu className="w-5 h-5" /></button>
          <img src={APP_ICON_URL} alt="أيقونة بنك العائلة الرسمية" className="h-12 w-12 object-contain" />
          <div className="min-w-0">
            <h1 className="hidden font-heading font-black text-lg sm:block">لوحة إدارة بنك العائلة</h1>
            <p className="hidden sm:block text-[10px] text-[var(--brand-champagne)] font-medium truncate">مركز الإدارة والتشغيل</p>
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2 sm:gap-3">
          <button
            onClick={switchToPersonal}
            disabled={switchingPersonal}
            className="fb-header-control min-h-10 px-3 sm:px-3.5 text-xs font-bold"
            title="الانتقال إلى حسابك الشخصي"
          >
            <UserRound className="h-4 w-4" /><span className="hidden sm:inline">حسابي الشخصي</span>
          </button>

          <button
            onClick={handleLogout}
            className="fb-header-control fb-header-control--danger min-h-10 px-3 sm:px-4 text-xs font-bold"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">تسجيل الخروج</span>
          </button>
        </div>
      </header>

      {/* Main Body */}
      <div className="flex-1 mb-page grid lg:grid-cols-[minmax(220px,260px)_minmax(0,1fr)] gap-5 lg:gap-8">
        {/* Sidebar Navigation */}
        <aside className="family-admin-sidebar mb-premium-surface hidden lg:block min-w-0 border text-[var(--color-text-primary)] rounded-2xl p-3 shadow-[var(--card-shadow)] h-fit lg:sticky top-24 max-h-[calc(100dvh-7rem)] overflow-y-auto">
          {renderNavigation()}
        </aside>

        {/* Content Area */}
        <main className="min-w-0 w-full">
          <ErrorBoundary key={location.pathname}>
            <Routes>
              <Route index element={<Navigate to={firstAllowedPath} replace />} />
              <Route path="users" element={guarded('users', <UsersPage />)} />
              <Route path="stats" element={guarded('dashboard', <StatsPage />)} />
              <Route path="subscriptions" element={guarded('subscriptions', <SubscriptionCenter />)} />
              <Route path="family-ai" element={<FamilyAiWorkspace kind="admin" />} />
              <Route path="family-ai-settings" element={access.loading ? <div className="p-5">جاري التحقق من صلاحيات الإدارة…</div> : access.fullAccess ? <FamilyAiSettings /> : <Navigate to="../family-ai" replace />} />
              <Route path="gamification" element={guarded('gamification', <GamificationAdminPage />)} />
              <Route path="bank-funds" element={guarded('bank_money', <BankFundsSection />)} />
              <Route path="operations-approvals" element={guarded('operations', <OperationsApprovalsSection />)} />
              <Route path="boxes-goals" element={guarded('boxes_goals', <BoxesAdminPage />)} />
              <Route path="finance" element={<Navigate to="../bank-funds" replace />} />
              <Route path="plans" element={<Navigate to="../subscriptions" replace />} />
              <Route path="logs" element={guarded('logs', <LogsCenter />)} />
              <Route path="external-money" element={guarded('outside_money', <ExternalMoneyAdminPage />)} />
              <Route path="marketplace" element={guarded('store', <MarketplacePage />)} />
              <Route path="notifications" element={guarded('notifications', <NotificationsAdminPage />)} />
              <Route path="backup" element={guarded('backup', <BackupPage />)} />
              <Route path="settings" element={guarded('settings', <SettingsPage />)} />
              <Route path="*" element={<Navigate to={`/admin/dashboard/${firstAllowedPath}`} replace />} />
            </Routes>
          </ErrorBoundary>
        </main>
      </div>
      {mobileNavOpen && <div className="lg:hidden fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label="قائمة الإدارة">
        <button className="absolute inset-0 bg-slate-950/55" onClick={() => setMobileNavOpen(false)} aria-label="إغلاق القائمة" />
        <aside className="family-admin-drawer absolute inset-y-0 end-0 w-[min(22rem,88vw)] bg-[var(--color-surface)] border-s border-[var(--color-border)] shadow-2xl p-4 overflow-y-auto fade-up">
          <div className="flex items-center justify-between mb-5"><div><div className="font-black text-[var(--color-text-primary)]">لوحة الإدارة</div><div className="text-xs text-[var(--color-text-secondary)] mt-1">التنقل بين أقسام النظام</div></div><button onClick={() => setMobileNavOpen(false)} className="w-10 h-10 rounded-xl border border-[var(--color-border)] flex items-center justify-center" aria-label="إغلاق"><X className="w-5 h-5" /></button></div>
          {renderNavigation(true)}
        </aside>
      </div>}
    </div></AdminAccessProvider>
  );
}
